/**
 * UnifiedDashboard — the post-login landing page (dashboardKey="home-v2").
 *
 * A landing page has a different job from an analytics page. It is opened many
 * times a day, usually on the way to somewhere else, and it has to answer two
 * questions fast: what do I have to act on, and how are we doing. The full
 * analytical surface lives on the Safety Overview page (/safety/dashboard),
 * which is linked from the header.
 *
 * Client feedback was that this page showed too much data. What it showed was
 * ~35 cards, of which six headline KPIs were repeated verbatim in the grouped
 * KPI set below them, plus two line charts of the same incident counts, and a
 * hero row reading "Insufficient Data" / "no data" / "0". The default layout
 * (see safetyDefaultLayoutFor) is now four widgets; everything else stays in
 * the catalog, one click away under Customize.
 *
 * The widget set follows PERMISSIONS, not the role string: `analytics:read`
 * gets the analytics snapshot, everyone else the personal widgets (My Work).
 */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Siren, Eye, BarChart3, ArrowRight } from 'lucide-react';
import { safetyDefaultLayoutFor, safetyDashboardAccess } from '@/dashboard/presets/safety';
import { usePermissions } from '../../context/PermissionContext';
import { useSite } from '../../context/SiteContext';
import SafetyDashboardView from './SafetyDashboardView';

function greeting(hour) {
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

/** "Admin User" → "Admin". A greeting uses the name someone is called by. */
function firstName(name) {
  return (name || '').trim().split(/\s+/)[0] || '';
}

function HomeHeader({ user }) {
  const { hasPermission } = usePermissions();
  const { activeSiteName } = useSite();
  const now = new Date();
  const who = firstName(user?.name);
  const when = now.toLocaleDateString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long',
  });

  // Same permission gates the routes themselves enforce — a shortcut must not
  // offer a screen the user would be bounced out of. (Server-side RBAC is
  // unchanged; this is only about not showing a dead button.)
  const canReportIncident = hasPermission('incidents', 'create');
  const canReportObservation = hasPermission('observations', 'create');
  const canSeeAnalytics = safetyDashboardAccess(hasPermission, user?.role).analytics;

  const action = 'inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors';

  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
      <div className="min-w-0">
        <h1 className="text-2xl font-bold text-gray-900">
          {greeting(now.getHours())}{who ? `, ${who}` : ''}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {activeSiteName ? <span className="font-medium text-gray-600">{activeSiteName}</span> : null}
          {activeSiteName ? ' · ' : ''}
          {when}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {canReportIncident && (
          <Link to="/report" className={`${action} bg-[#1B5C94] text-white hover:bg-[#164b79]`}>
            <Siren className="h-4 w-4" /> Report Incident
          </Link>
        )}
        {canReportObservation && (
          <Link to="/observations/create"
            className={`${action} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}>
            <Eye className="h-4 w-4" /> Report Observation
          </Link>
        )}
        {canSeeAnalytics && (
          // The deep analytics did not disappear with the lean default — this
          // says where they went, which is the difference between "trimmed"
          // and "lost".
          <Link to="/safety/dashboard"
            className="group inline-flex items-center gap-1.5 px-2 py-2 text-sm font-medium text-gray-500 hover:text-gray-800">
            <BarChart3 className="h-4 w-4" /> Full analytics
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </Link>
        )}
      </div>
    </div>
  );
}

const UnifiedDashboard = ({ user, onLogout }) => {
  const { hasPermission } = usePermissions();
  const access = useMemo(
    () => safetyDashboardAccess(hasPermission, user?.role), [hasPermission, user?.role],
  );
  return (
    <SafetyDashboardView
      user={user}
      onLogout={onLogout}
      dashboardKey="home-v2"
      title="Dashboard"
      defaultLayout={safetyDefaultLayoutFor(access)}
      header={<HomeHeader user={user} />}
    />
  );
};

export default UnifiedDashboard;
